export type PineDiagnosticSeverity = "error" | "warning";

export type PineDiagnostic = {
  code: string;
  severity: PineDiagnosticSeverity;
  message: string;
  line?: number;
};

export type PineValidationReport = {
  status: "pass" | "warning" | "fail";
  diagnostics: PineDiagnostic[];
};

function lineOf(code: string, pattern: RegExp): number | undefined {
  const lines = code.split("\n");
  const index = lines.findIndex((line) => pattern.test(line));
  return index >= 0 ? index + 1 : undefined;
}

function hasBalancedDelimiters(code: string): boolean {
  let depth = 0;
  let inString = false;
  for (let i = 0; i < code.length; i += 1) {
    const ch = code[i];
    const prev = code[i - 1];
    if (ch === '"' && prev !== "\\") {
      inString = !inString;
      continue;
    }
    if (inString) continue;
    if (ch === "(") depth += 1;
    if (ch === ")") depth -= 1;
    if (depth < 0) return false;
  }
  return depth === 0 && !inString;
}

function diagnostic(
  diagnostics: PineDiagnostic[],
  severity: PineDiagnosticSeverity,
  code: string,
  message: string,
  line?: number,
): void {
  diagnostics.push({ severity, code, message, line });
}

export function validatePineStrategy(code: string): PineValidationReport {
  const diagnostics: PineDiagnostic[] = [];
  const trimmed = code.trim();

  if (!trimmed.startsWith("//@version=5")) {
    diagnostic(
      diagnostics,
      "error",
      "missing_version",
      "Pine drafts must start with //@version=5.",
      1,
    );
  }
  if (!/\b(strategy|indicator)\s*\(/.test(code)) {
    diagnostic(
      diagnostics,
      "error",
      "missing_declaration",
      "Pine drafts must declare strategy() or indicator().",
      lineOf(code, /\/\/@version=5/) ?? 1,
    );
  }
  if (!hasBalancedDelimiters(code)) {
    diagnostic(
      diagnostics,
      "error",
      "unbalanced_parentheses",
      "Pine draft has unmatched parentheses or string delimiters.",
    );
  }
  if (/\balertcondition\s*\(\s*(longSignal|shortSignal)\b/.test(code)) {
    diagnostic(
      diagnostics,
      "error",
      "raw_signal_alert",
      "Alerts must use activeLongSignal/activeShortSignal so disabled sides cannot alert.",
      lineOf(code, /\balertcondition\s*\(\s*(longSignal|shortSignal)\b/),
    );
  }
  if (/^\s*if\s+(longSignal|shortSignal)\b/m.test(code)) {
    diagnostic(
      diagnostics,
      "error",
      "raw_signal_entry",
      "Entries must use activeLongSignal/activeShortSignal so disabled sides cannot trade.",
      lineOf(code, /^\s*if\s+(longSignal|shortSignal)\b/m),
    );
  }
  if (/\bstrategy\.entry\s*\(/.test(code) && !/\bstrategy\.exit\s*\(/.test(code)) {
    diagnostic(
      diagnostics,
      "error",
      "missing_exit",
      "Strategy entries must include strategy.exit() risk controls.",
      lineOf(code, /\bstrategy\.entry\s*\(/),
    );
  }
  if (/\bactive(Long|Short)Signal\b/.test(code)) {
    if (!/\blongEnabled\s*=\s*input\.bool\(/.test(code)) {
      diagnostic(
        diagnostics,
        "error",
        "missing_long_toggle",
        "Active signal gating requires a longEnabled input.",
        lineOf(code, /\bactiveLongSignal\b/),
      );
    }
    if (!/\bshortEnabled\s*=\s*input\.bool\(/.test(code)) {
      diagnostic(
        diagnostics,
        "error",
        "missing_short_toggle",
        "Active signal gating requires a shortEnabled input.",
        lineOf(code, /\bactiveShortSignal\b/),
      );
    }
  }

  diagnostic(
    diagnostics,
    "warning",
    "compiler_not_verified",
    "This is static validation only; TradingView compilation has not been verified.",
  );

  const hasErrors = diagnostics.some((d) => d.severity === "error");
  const hasWarnings = diagnostics.some((d) => d.severity === "warning");
  return {
    status: hasErrors ? "fail" : hasWarnings ? "warning" : "pass",
    diagnostics,
  };
}
